// src/components/SpeechRecognitionApp.js
import React, { useState, useEffect, useRef } from 'react';
import LogoutButton from './LogoutButton';

const SpeechRecognitionApp = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [editableTranscript, setEditableTranscript] = useState('');
  const [response, setResponse] = useState('');
  const [prefixText, setPrefixText] = useState(
    'These questions will be of Web Development - '
  );
  const recognitionRef = useRef(null);
  const restartRef = useRef(false);
  const recognitionActiveRef = useRef(false);
  const silenceTimeoutRef = useRef(null);

  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert('Your browser does not support Speech Recognition.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;

    recognition.lang = 'en-US';
    recognition.continuous = false;
    recognition.interimResults = true;

    recognition.onstart = () => {
      setIsRecording(true);
      recognitionActiveRef.current = true;
    };

    recognition.onresult = (event) => {
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }

      const speechToText = Array.from(event.results)
        .map((result) => result[0].transcript)
        .join('');
      setTranscript(speechToText);
      setEditableTranscript(speechToText);

      if (!event.results[event.results.length - 1].isFinal) {
        silenceTimeoutRef.current = setTimeout(() => {
          if (recognitionActiveRef.current) {
            recognitionRef.current.stop();
            restartRef.current = false;
            setIsRecording(false);
            recognitionActiveRef.current = false;
            handleGroqAI(speechToText);
          }
        }, 2000);
      } else {
        handleGroqAI(speechToText);
        recognitionRef.current.stop();
        restartRef.current = false;
        setIsRecording(false);
        recognitionActiveRef.current = false;
      }
    };

    recognition.onend = () => {
      setIsRecording(false);
      recognitionActiveRef.current = false;

      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }

      if (restartRef.current && !recognitionActiveRef.current) {
        recognitionRef.current.start();
      }
    };

    recognition.onerror = (e) => {
      console.error('Recognition Error:', e.error);
      setIsRecording(false);
      recognitionActiveRef.current = false;
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }
      if (restartRef.current) {
        setTimeout(() => {
          if (!recognitionActiveRef.current) {
            recognitionRef.current.start();
          }
        }, 1000);
      }
    };

    return () => {
      recognition.stop();
      recognitionActiveRef.current = false;
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }
    };
  }, []);

  const handleStart = () => {
    if (!recognitionRef.current || recognitionActiveRef.current)
      return;
    restartRef.current = true;
    recognitionRef.current.start();
  };

  const handleStop = () => {
    if (!recognitionRef.current || !recognitionActiveRef.current)
      return;
    restartRef.current = false;
    recognitionRef.current.stop();
    setIsRecording(false);
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
    }
  };

  const handleTranscriptEdit = (e) => {
    setEditableTranscript(e.target.value);
  };

  const handlePrefixChange = (e) => {
    setPrefixText(e.target.value);
  };

  const handleResubmit = () => {
    if (editableTranscript.trim()) {
      setTranscript(editableTranscript);
      handleGroqAI(editableTranscript);
    }
  };

  const handleGroqAI = async (text) => {
    const hiddenInstruction =
      ' Give short answer in 6 single line bullet points.';
    const fullText = prefixText + text + hiddenInstruction;
    setResponse('Thinking...');

    try {
      const res = await fetch(
        'https://api.groq.com/openai/v1/chat/completions',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer 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`,
            // Authorization: `Bearer ${process.env.REACT_APP_GROQ_API_KEY}`,
          },
          body: JSON.stringify({
            // Updated to use a currently supported model
            model: 'llama-3.1-8b-instant', // or 'llama-3.1-70b-versatile', 'mixtral-8x7b-32768'
            messages: [
              {
                role: 'system',
                content: 'You are a helpful assistant.',
              },
              { role: 'user', content: fullText },
            ],
          }),
        }
      );

      const data = await res.json();
      setResponse(data.choices[0]?.message?.content || 'No response');
    } catch (error) {
      console.error(error);
      setResponse('Error contacting Groq AI.');
    }
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex flex-col items-center justify-center px-4 py-8'>
      <LogoutButton />
      <div className='absolute inset-0 overflow-hidden'>
        <div className='absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse'></div>
        <div className='absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse'></div>
      </div>

      <div className='relative bg-white/10 backdrop-blur-lg border border-white/20 shadow-2xl rounded-3xl p-8 w-full max-w-2xl'>
        <div className='text-center mb-8'>
          <div className='inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full mb-4 shadow-lg'>
            <svg
              className='w-8 h-8 text-white'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z'
              />
            </svg>
          </div>
        </div>

        <div className='mb-6'>
          <div className='bg-black/50 border border-white/20 rounded-xl p-4'>
            <h3 className='text-white font-semibold mb-2'>
              Prefix Text:
            </h3>
            <input
              type='text'
              value={prefixText}
              onChange={handlePrefixChange}
              className='w-full p-2 rounded bg-black/30 text-white border border-white/20'
              placeholder='Enter prefix text for AI prompt'
            />
            <p className='text-white/60 text-sm mt-2'>
              This text will be prepended to your speech input when
              sending to the AI.
            </p>
          </div>
        </div>

        <div className='flex justify-center gap-4 mb-8'>
          <button
            onClick={handleStart}
            disabled={isRecording}
            className='px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-2xl font-semibold shadow-lg hover:scale-105 transition-transform disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed'
          >
            Start Listening
          </button>
          <button
            onClick={handleStop}
            disabled={!isRecording}
            className='px-8 py-4 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-2xl font-semibold shadow-lg hover:scale-105 transition-transform disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed'
          >
            Stop
          </button>
        </div>

        {isRecording && (
          <div className='text-center mb-6'>
            <div className='inline-flex items-center gap-3 px-6 py-3 bg-red-500/20 border border-red-500/30 rounded-full'>
              <div className='w-3 h-3 bg-red-500 rounded-full animate-pulse'></div>
              <span className='text-red-300 font-medium'>
                Recording in progress...
              </span>
            </div>
          </div>
        )}

        {transcript && (
          <div className='mb-6'>
            <div className='bg-black/50 border border-white/20 rounded-xl p-4'>
              <h3 className='text-white font-semibold mb-2'>
                You said:
              </h3>
              <textarea
                value={editableTranscript}
                onChange={handleTranscriptEdit}
                className='w-full p-2 rounded bg-black/30 text-white border border-white/20 min-h-[100px]'
              />
              <button
                onClick={handleResubmit}
                className='mt-3 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded hover:opacity-90'
              >
                Resubmit
              </button>
            </div>
          </div>
        )}

        {response && (
          <div className='bg-black/50 border border-purple-500/20 rounded-xl p-4'>
            <h3 className='text-white font-semibold mb-2'>
              AI Response:
            </h3>
            <div className='text-white/90 whitespace-pre-line'>
              {response === 'Thinking...' ? (
                <div className='flex items-center gap-2'>
                  <div className='flex space-x-1'>
                    <div className='w-2 h-2 bg-white rounded-full animate-bounce'></div>
                    <div className='w-2 h-2 bg-white rounded-full animate-bounce delay-100'></div>
                    <div className='w-2 h-2 bg-white rounded-full animate-bounce delay-200'></div>
                  </div>
                  <span className='text-white/70'>
                    Processing your request...
                  </span>
                </div>
              ) : (
                response
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpeechRecognitionApp;
